import modulesConfig from "./modules.json";
import type { ModuleDefinition, ModuleMenuDefinition, ModuleRegistry } from "./types";
import { fetchModuleRegistry } from "./api";
import { isModuleOpen } from "@/uptk/core/modules/activeModules";
import type { AuthContextResponse, ModuleDefinitionDTO } from "@/types/auth_context";

type ModulesConfig = ModuleRegistry & {
  modules?: ModuleDefinition[];
};

export type ModuleMenuItem = {
  key: string;
  label: string;
  route: string;
  portalRoute?: string;
  order: number;
  moduleCode: string;
};

export type ModuleParameterMenuItem = {
  moduleCode: string;
  moduleName?: string;
  order: number;
  menu: ModuleMenuDefinition;
};

export type ModuleTopMenuContext = "portal" | "adminportal";
const allowedLandingPortals: ModuleTopMenuContext[] = ["portal", "adminportal"];

const config = modulesConfig as ModulesConfig;

const safeNumber = (value: unknown, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const mergeModules = (base: ModuleDefinition, override: ModuleDefinition) => {
  const resolvedMenus = Array.isArray(override.menus) && override.menus.length > 0
    ? override.menus
    : base.menus;
  const resolvedLandingMenus =
    Array.isArray(override.landingMenus) && override.landingMenus.length > 0
      ? override.landingMenus
      : base.landingMenus;
  const resolvedFeatures =
    Array.isArray(override.features) && override.features.length > 0
      ? override.features
      : base.features;

  return {
    ...base,
    ...override,
    menus: resolvedMenus,
    landingMenus: resolvedLandingMenus,
    features: resolvedFeatures,
  };
};

const mergeRegistry = (override: ModulesConfig): ModulesConfig => {
  const baseModules = Array.isArray(config.modules) ? config.modules : [];
  const overrideModules = Array.isArray(override.modules) ? override.modules : [];
  if (overrideModules.length === 0) {
    return { modules: baseModules };
  }
  const merged = baseModules.map((module) => {
    const match = overrideModules.find((item) => item.code === module.code);
    return match ? mergeModules(module, match) : module;
  });
  const extras = overrideModules.filter(
    (item) => !baseModules.some((module) => module.code === item.code)
  );
  return { modules: [...merged, ...extras] };
};

const getRegistry = (): ModulesConfig => {
  if (typeof window === "undefined") {
    return { modules: Array.isArray(config.modules) ? config.modules : [] };
  }
  const stored = localStorage.getItem("modules_registry");
  if (!stored) {
    return { modules: Array.isArray(config.modules) ? config.modules : [] };
  }
  try {
    const parsed = JSON.parse(stored) as ModulesConfig;
    if (!parsed || typeof parsed !== "object") {
      return { modules: Array.isArray(config.modules) ? config.modules : [] };
    }
    return mergeRegistry(parsed);
  } catch {
    return { modules: Array.isArray(config.modules) ? config.modules : [] };
  }
};

export const setModuleRegistry = (registry: ModulesConfig) => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem("modules_registry", JSON.stringify(registry));
};

export const loadModuleRegistry = async (tenantId?: string) => {
  try {
    const registry = await fetchModuleRegistry({ tenantId });
    setModuleRegistry(registry);
    return registry;
  } catch {
    return getRegistry();
  }
};

/**
 * Transform backend ModuleDefinitionDTO to frontend ModuleDefinition
 * Handles conversion of feature arrays and menu structures
 */
const transformAuthContextModule = (dto: ModuleDefinitionDTO): ModuleDefinition => {
  return {
    code: dto.code,
    name: dto.name,
    domain: dto.domain,
    features: dto.features?.map(f => f.key) || [],
    menus: (dto.menus as ModuleMenuDefinition[]) || [],
    landingMenus: (dto.landingMenus as ModuleMenuDefinition[]) || []
  };
};

/**
 * Load module registry from AuthContext response
 * This is the primary method for loading modules from the backend
 *
 * @param authContext - Auth context from /api/v1/auth/context
 * @returns Module registry
 *
 * @example
 * const authContext = await fetchAuthContext();
 * const registry = loadModuleRegistryFromAuthContext(authContext);
 */
export const loadModuleRegistryFromAuthContext = (
  authContext: AuthContextResponse
): ModulesConfig => {
  if (!authContext.modules || authContext.modules.length === 0) {
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.warn('[Registry] Using fallback modules.json - backend context unavailable');
    }
    return getRegistry();
  }

  const registry: ModuleRegistry = {
    modules: authContext.modules.map(transformAuthContextModule)
  };

  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    const summary = registry.modules.map(m => ({
      code: m.code,
      domain: m.domain,
      landingMenus: (m.landingMenus ?? []).map(lm => ({ key: lm.key, label: lm.label, route: lm.route, portals: lm.portals })),
      menus: (m.menus ?? []).length,
    }));
    console.group('[Registry] Loaded from auth context');
    console.table(summary);
    console.groupEnd();
  }

  setModuleRegistry(registry);
  return registry;
};

const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, "");

const joinPortalRoute = (portal: string, route: string): string => {
  const normalizedPortal = trimSlashes(portal);
  const normalizedRoute = `/${trimSlashes(route)}`;
  return `/${normalizedPortal}${normalizedRoute}`;
};

const resolveContextRoute = (
  menu: ModuleMenuDefinition,
  context: ModuleTopMenuContext
): string | undefined => {
  const menuWithPortals = menu as ModuleMenuDefinition & { portals?: string[] };
  const explicitPortals = Array.isArray(menuWithPortals.portals)
    ? menuWithPortals.portals
        .map((value) => value.trim().toLowerCase())
        .filter(
          (value): value is ModuleTopMenuContext =>
            allowedLandingPortals.includes(value as ModuleTopMenuContext)
        )
    : [];

  // When portals is explicitly set, use strict filtering
  if (explicitPortals.length > 0) {
    if (!explicitPortals.includes(context)) {
      if (typeof window !== 'undefined' && import.meta.env.DEV) {
        console.debug(`[Registry] resolveContextRoute: '${menu.key}' portals=[${explicitPortals.join(',')}] does not include context='${context}' → skipped`);
      }
      return undefined;
    }
  } else {
    // Infer portal availability from route/portalRoute conventions:
    //   - If portalRoute exists → item is available in "portal" context
    //   - If route starts with /adminportal/ → item is available in "adminportal" context
    //   - If route is a relative path → item is available in both contexts
    //   - If none of the above match → item is not available in this context
    const hasPortalRoute = Boolean(
      (menu as ModuleMenuDefinition & { portalRoute?: string }).portalRoute
    );
    const route = (menu.route ?? "").trim();
    const hasAdminportalRoute = route.startsWith("/adminportal/");
    const isRelativeRoute = route.length > 0 && !route.startsWith("/");
    // Parameterized routes (e.g. "/:workspaceId/:year/finance") are available in both contexts
    const isParameterizedRoute = /^\/:/.test(route);

    const inferredAvailable =
      (context === "portal" && (hasPortalRoute || isRelativeRoute || isParameterizedRoute)) ||
      (context === "adminportal" && (hasAdminportalRoute || isRelativeRoute || isParameterizedRoute));

    if (!inferredAvailable) {
      if (typeof window !== 'undefined' && import.meta.env.DEV) {
        console.debug(`[Registry] resolveContextRoute: '${menu.key}' route='${route}' hasPortalRoute=${hasPortalRoute} hasAdminportalRoute=${hasAdminportalRoute} isRelativeRoute=${isRelativeRoute} → not available in context='${context}' → skipped`);
      }
      return undefined;
    }
  }

  if (!menu.route || menu.route.trim().length === 0) {
    return undefined;
  }

  const rawRoute = menu.route.trim();

  // For portal context, prefer portalRoute if available
  if (context === "portal") {
    const portalRoute = (menu as ModuleMenuDefinition & { portalRoute?: string }).portalRoute;
    if (portalRoute && portalRoute.trim().length > 0) {
      return portalRoute.trim();
    }
  }

  if (rawRoute.startsWith("/portal/") || rawRoute.startsWith("/adminportal/")) {
    return rawRoute;
  }
  const resolved = joinPortalRoute(context, rawRoute);
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    console.debug(`[Registry] resolveContextRoute: '${menu.key}' context='${context}' rawRoute='${rawRoute}' → resolved='${resolved}'`);
  }
  return resolved;
};

export const getModuleMenuItems = (): ModuleMenuItem[] => {
  const registry = getRegistry();
  const modules = Array.isArray(registry.modules) ? registry.modules : [];
  const items: ModuleMenuItem[] = [];
  modules.forEach((module, moduleIndex) => {
    const menus = Array.isArray(module.menus) ? module.menus : [];
    menus.forEach((menu, menuIndex) => {
      if (!menu.key || !menu.label || !menu.route) {
        return;
      }
      items.push({
        key: menu.key,
        label: menu.label,
        route: menu.route,
        portalRoute: menu.portalRoute,
        order: safeNumber(menu.order, moduleIndex * 10 + menuIndex),
        moduleCode: module.code,
      });
    });
  });

  return items.sort((a, b) => a.order - b.order);
};

/**
 * Extended landing menu item with sub-menu labels for flyout descriptions.
 */
export type ModuleLandingMenuItem = ModuleMenuItem & {
  /** Domain of the owning module (e.g. "finance", "cbm") */
  domain?: string;
  /** Sub-menu labels for flyout description text */
  subMenuLabels: string[];
  /** Full sub-menu items */
  subMenus: ModuleMenuItem[];
};

export const getModuleLandingTopMenuItemsExtended = (context: ModuleTopMenuContext): ModuleLandingMenuItem[] => {
  const registry = getRegistry();
  const modules = Array.isArray(registry.modules) ? registry.modules : [];
  // UPT-2069 (L1) : l'auth-context décide (isModuleOpen) ; plus de « tout passe » sans snapshot.
  const isModuleActive = (moduleCode: string) => isModuleOpen(moduleCode);

  const items: ModuleLandingMenuItem[] = [];

  modules.forEach((module, moduleIndex) => {
    if (!isModuleActive(module.code)) return;

    const landingMenus = Array.isArray(module.landingMenus) ? module.landingMenus : [];
    landingMenus.forEach((menu, menuIndex) => {
      if (!menu.key || !menu.label) return;
      const preferredRoute = resolveContextRoute(menu, context);
      if (!preferredRoute) return;

      // Collect sub-menus for description
      const subMenuDefs = Array.isArray(module.menus) ? module.menus : [];
      const subMenus: ModuleMenuItem[] = subMenuDefs
        .filter((sm) => sm.key && sm.label)
        .map((sm, smIdx) => ({
          key: sm.key,
          label: sm.label,
          route: sm.route ?? "",
          portalRoute: sm.portalRoute,
          order: safeNumber(sm.order, smIdx),
          moduleCode: module.code,
        }))
        .sort((a, b) => a.order - b.order);

      const subMenuLabels = subMenus.map((sm) => sm.label);

      items.push({
        key: menu.key,
        label: menu.label,
        route: preferredRoute,
        portalRoute: menu.portalRoute,
        order: safeNumber(menu.order, moduleIndex * 10 + menuIndex),
        moduleCode: module.code,
        domain: module.domain,
        subMenuLabels,
        subMenus,
      });
    });
  });

  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    console.group(`[Registry] getModuleLandingTopMenuItemsExtended(context='${context}')`);
    console.log(`Resolved ${items.length} landing menu item(s) from ${modules.length} module(s)`);
    if (items.length === 0) {
      const skippedReasons = modules.map(m => ({
        code: m.code,
        active: isModuleActive(m.code),
        landingMenus: (m.landingMenus ?? []).map(lm => lm.key),
      }));
      console.table(skippedReasons);
    }
    console.groupEnd();
  }

  return items.sort((a, b) => a.order - b.order);
};

export const getModuleLandingTopMenuItems = (context: ModuleTopMenuContext): ModuleMenuItem[] => {
  const registry = getRegistry();
  const modules = Array.isArray(registry.modules) ? registry.modules : [];
  // UPT-2069 (L1) : l'auth-context décide (isModuleOpen) ; plus de « tout passe » sans snapshot.
  const isModuleActive = (moduleCode: string) => isModuleOpen(moduleCode);

  const items: ModuleMenuItem[] = [];

  modules.forEach((module, moduleIndex) => {
    if (!isModuleActive(module.code)) {
      return;
    }

    const landingMenus = Array.isArray(module.landingMenus) ? module.landingMenus : [];
    landingMenus.forEach((menu, menuIndex) => {
      if (!menu.key || !menu.label) {
        return;
      }
      const preferredRoute = resolveContextRoute(menu, context);
      if (!preferredRoute) {
        return;
      }
      items.push({
        key: menu.key,
        label: menu.label,
        route: preferredRoute,
        portalRoute: menu.portalRoute,
        order: safeNumber(menu.order, moduleIndex * 10 + menuIndex),
        moduleCode: module.code,
      });
    });
  });

  return items.sort((a, b) => a.order - b.order);
};

/**
 * @deprecated Use getMenuFeatures from get_features_v3.ts instead
 * Will be removed in v3.2.0
 */
export const getModuleFeatureKeys = (moduleCode: string): string[] => {
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    console.warn('[registry] getModuleFeatureKeys is deprecated. Use getMenuFeatures from get_features_v3.ts instead.');
  }
  const registry = getRegistry();
  const modules = Array.isArray(registry.modules) ? registry.modules : [];
  const match = modules.find((module) => module.code === moduleCode);
  return Array.isArray(match?.features) ? match!.features : [];
};

/**
 * @deprecated Use getMenuFeatures from get_features_v3.ts instead
 * Will be removed in v3.2.0
 */
export const getModuleLandingMenus = (moduleCode: string): ModuleMenuDefinition[] => {
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    console.warn('[registry] getModuleLandingMenus is deprecated. Use getMenuFeatures from get_features_v3.ts instead.');
  }
  const registry = getRegistry();
  const modules = Array.isArray(registry.modules) ? registry.modules : [];
  const match = modules.find((module) => module.code === moduleCode);
  const menus = Array.isArray(match?.landingMenus) ? match!.landingMenus : [];
  return menus.slice().sort((a, b) => safeNumber(a.order, 0) - safeNumber(b.order, 0));
};

export const getModuleMenus = (moduleCode: string): ModuleMenuDefinition[] => {
  const registry = getRegistry();
  const modules = Array.isArray(registry.modules) ? registry.modules : [];
  const match = modules.find((module) => module.code === moduleCode);
  const menus = Array.isArray(match?.menus) ? match!.menus : [];
  return menus.slice().sort((a, b) => safeNumber(a.order, 0) - safeNumber(b.order, 0));
};

export const getModuleParameterMenus = (): ModuleParameterMenuItem[] => {
  const registry = getRegistry();
  const modules = Array.isArray(registry.modules) ? registry.modules : [];
  const items: ModuleParameterMenuItem[] = [];
  modules.forEach((module, moduleIndex) => {
    const landingMenus = Array.isArray(module.landingMenus) ? module.landingMenus : [];
    const parameterMenu = findLandingMenu(landingMenus, "parameter");
    if (!parameterMenu) {
      return;
    }
    items.push({
      moduleCode: module.code,
      moduleName: module.name,
      menu: parameterMenu,
      order: safeNumber(parameterMenu.order, moduleIndex * 10),
    });
  });
  return items.sort((a, b) => a.order - b.order);
};

export const findLandingMenu = (
  menus: ModuleMenuDefinition[],
  key: string
): ModuleMenuDefinition | null => {
  const normalized = key.trim().toLowerCase();
  for (const menu of menus) {
    if (menu.key.toLowerCase() === normalized) {
      return menu;
    }
    if (Array.isArray(menu.menus)) {
      const match = findLandingMenu(menu.menus, normalized);
      if (match) {
        return match;
      }
    }
  }
  return null;
};

/**
 * Find a module by its domain name.
 * Returns the first module whose domain matches (case-insensitive).
 */
export const getModuleByDomain = (domain: string): ModuleDefinition | null => {
  const registry = getRegistry();
  const modules = Array.isArray(registry.modules) ? registry.modules : [];
  const normalized = domain.trim().toLowerCase();
  return modules.find((module) => module.domain?.toLowerCase() === normalized) ?? null;
};
